import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter from "../../components/LHFilter";
import LHDataTable from "../../components/LHDataTable";
import { FilterItem } from "../../components/LHFilter";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

// Helper to format period/week display
const formatPeriodWeek = (item: any): string => {
  const period = item?.period ?? "";
  const week = item?.week ?? "";
  if (period && week) return `P${period}W${week}`;
  if (period) return `P${period}`;
  if (week) return `W${week}`;
  return "-";
};

// NOTE: iOS mobile detection is computed inside the component as `isMobileIos` to avoid
// module-level evaluation / hoisting issues that can make the helper undefined at runtime.

interface SparcWalkMetricsProps {
  filterOptions: FilterItem[];
  handleFilterChange: (key: string, value: any) => void;
  isLoading: boolean;
  apiError: string | null;
  sparcWalkData?: any[];
  tableColumns: any[];
  handleRowClick: (rowData: any) => void;
  tableData: any[];
  isMobile: boolean;
  // applyFilters is handled by parent auto-apply; no direct use here
  // applyFilters: () => Promise<void>;
  resetFilters?: () => Promise<void>;
  // Mobile filter props
  showMobileFilterButton?: boolean;
  onMobileFilterPress?: () => void;
}

const SparcWalkMetrics: React.FC<SparcWalkMetricsProps> = ({
  filterOptions,
  handleFilterChange,
  isLoading,
  apiError,
  tableColumns,
  handleRowClick,
  tableData,
  resetFilters,
  showMobileFilterButton = false,
  onMobileFilterPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const customizedColumns = React.useMemo(() => {
    const hasPeriodData = tableData.some(
      (item) => "period" in item && item.period !== undefined
    );
    const hasWeekData = tableData.some(
      (item) => "week" in item && item.week !== undefined
    );

    const hasPeriod = tableColumns.some((col) => col.columnKey === "period");
    const hasWeek = tableColumns.some((col) => col.columnKey === "week");
    const hasCombined = tableColumns.some(
      (col) => col.columnKey === "periodWeek"
    );
    const shouldCombine = hasPeriod && hasWeek && !hasCombined;

    let combinedCreated = false;
    let result = [...tableColumns];

    if (
      !hasPeriod &&
      !hasWeek &&
      !hasCombined &&
      (hasPeriodData || hasWeekData)
    ) {
      result = [
        ...result.slice(0, 1),
        {
          columnKey: "periodWeek",
          header: "Period Week",
          getSortValue: (item: any) => {
            const p = Number(item.period) || 0;
            const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
            return p * 100 + w;
          },
          renderCell: (item: any) => (
              <Text
                style={[styles.cellText, isDark && { color: colors.text.primary }]}
              >
                {formatPeriodWeek(item)}
              </Text>
          ),
        },
        ...result.slice(1),
      ];
    }

    return result
      .map((column) => {
        if (column.columnKey === "details") {
          return {
            ...column,
            renderCell: (item: any) => (
              <Text
                style={[styles.detailsCell, isDark && { color: colors.brand.primary }]}
                onPress={() =>
                  handleRowClick({ ...item, columnKey: "details" })
                }
              >
                {item.details || "-"}
              </Text>
            ),
          };
        }

        if (column.columnKey === "periodWeek") {
          return {
            ...column,
            getSortValue: (item: any) => {
              const p = Number(item.period) || 0;
              const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
              return p * 100 + w;
            },
            renderCell: (item: any) => (
              <Text
                style={[styles.cellText, isDark && { color: colors.text.primary }]}
              >
                {formatPeriodWeek(item)}
              </Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "period") {
          combinedCreated = true;
          return {
            ...column,
            columnKey: "periodWeek",
            header: "Period Week",
            getSortValue: (item: any) => {
              const p = Number(item.period) || 0;
              const w = Number(String(item.week ?? "").replace(/^W/, "")) || 0;
              return p * 100 + w;
            },
            renderCell: (item: any) => (
              <Text
                style={[styles.cellText, isDark && { color: colors.text.primary }]}
              >
                {formatPeriodWeek(item)}
              </Text>
            ),
          };
        }

        if (shouldCombine && column.columnKey === "week" && combinedCreated)
          return null;

        return column;
      })
      .filter((col) => col !== null);
  }, [tableColumns, tableData, handleRowClick, isDark, colors.text.primary, colors.brand.primary]);

  // Pagination state for iOS mobile card view
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // always show 10 records per mobile page as requested
  const [displayStart, setDisplayStart] = useState(1); // window start for the two-number cells

  useEffect(() => {
    // Reset to first page whenever the table data changes
    setCurrentPage(1);
    setDisplayStart(1);
  }, [tableData]);

  // Ensure currentPage does not exceed total pages when data shrinks
  const totalPages = Math.max(
    1,
    Math.ceil((tableData?.length || 0) / rowsPerPage)
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Compute iOS mobile boolean inside the component to avoid module-level reference issues
  const windowWidth = Dimensions.get("window").width;
  const isMobileIos = Platform.OS === "ios" && windowWidth < 768;

  const paginatedData = isMobileIos
    ? tableData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : tableData;

  return (
    <View
      style={[
        styles.metricsWrapper,
        isDark && { backgroundColor: colors.background.page },
      ]}
    >
      <View
        style={[
          styles.dataTableContainer,
          isMobileIos && styles.dataTableContainerMobile,
          isDark && { backgroundColor: colors.background.surface },
        ]}
      >
        <View
          style={[
            styles.headerBox,
            isMobileIos && styles.headerBoxMobileSpacing,
            isMobileIos && showMobileFilterButton && styles.headerBoxIOSMobile,
          ]}
        >
          {/* On iOS mobile we want the title and Filter inline and the title
              allowed to wrap to two lines. For non-mobile the table header is
              supplied to LHDataTable via the tableHeader prop. */}
          <View
            style={[
              styles.headerTitleContainer,
              isMobileIos && styles.headerTitleMobile,
            ]}
          >
            <Text
              style={[styles.headerTitle, { color: isDark ? "#FFFFFF" : "#000000" }]}
              numberOfLines={isMobileIos ? 2 : undefined}
              ellipsizeMode={isMobileIos ? "tail" : undefined}
            >
              SPARC Walk List
            </Text>
          </View>

          {showMobileFilterButton && isMobileIos && (
            <TouchableOpacity
              style={[
                styles.mobileFilterButton,
                isDark && {
                  backgroundColor: colors.brand.primary,
                  borderColor: colors.brand.primary,
                },
              ]}
              onPress={onMobileFilterPress}
            >
              <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
              <Text style={styles.mobileFilterButtonText}>Filter</Text>
            </TouchableOpacity>
          )}
        </View>
        {/* Hide regular filters when mobile filter button is shown */}
        {!showMobileFilterButton && (
          <View style={styles.filterContainer}>
            {/* Apply filters immediately on selection change (no Apply button) */}
            <LHFilter
              filters={filterOptions}
              onFilterChange={(key: string, value: any) => {
                // call parent handler to update local filter state
                // actual application of the filters is handled by the parent (auto-apply)
                handleFilterChange(key, value);
              }}
              itemsPerRow={getResponsiveItemsPerRow(4)}
              onClearAllFilters={resetFilters}
            />
          </View>
        )}

        {isLoading && (
          <View style={[styles.messageContainer, isDark && { backgroundColor: colors.background.page }]}> 
            <Text style={[styles.loadingText, isDark && { color: colors.text.secondary }]}>Loading sparc walk data...</Text>
          </View>
        )}

        {apiError && (
          <View style={[styles.messageContainer, isDark && { backgroundColor: colors.background.page }]}>
            <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>Error: {apiError}</Text>
          </View>
        )}

        {!isLoading && (
          <>
            {tableData.length === 0 ? (
              <View style={[styles.messageContainer, isDark && { backgroundColor: colors.background.page }]}>
                <Text style={isDark ? { color: colors.text.primary } : undefined}>No records found</Text>
              </View>
            ) : isMobileIos ? (
              // Card layout for iOS mobile
              <View style={styles.cardsContainer}>
                {paginatedData.map((item, index) => (
                  <React.Fragment key={`card-${item.id || index}`}>
                    <CardItem
                      item={item}
                      columns={customizedColumns}
                      onRowClick={handleRowClick}
                    />
                    {index < paginatedData.length - 1 && <CardSeparator />}
                  </React.Fragment>
                ))}

                {/* Mobile pagination controls (use same UX as LHDataTable mobile pagination) */}
                <View style={styles.mobilePaginationContainer}>
                  <View style={[styles.mobilePaginationBox, isDark && { borderColor: colors.border.default }]}>
                    <TouchableOpacity
                      disabled={displayStart === 1 && currentPage === 1}
                      onPress={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        // move window so it starts at the new page
                        setDisplayStart(Math.max(1, newPage));
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart === 1 &&
                          currentPage === 1 &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-prev-page-button"
                      accessibilityLabel="Previous page"
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={16}
                        color={
                          displayStart === 1 && currentPage === 1
                            ? "#A0A0A0"
                            : isDark
                            ? colors.brand.primary
                            : "#005CBC"
                        }
                      />
                    </TouchableOpacity>

                    {/* Two-number window: displayStart and displayStart+1 (cap by totalPages). For single page, show only current page in first cell and empty second cell */}
                    {(() => {
                      const a = displayStart;
                      const b = Math.min(totalPages, displayStart + 1);
                      const aIsActive = currentPage === a;
                      const bIsActive = currentPage === b;
                      const isSinglePage = totalPages === 1;

                      return (
                        <>
                          <TouchableOpacity
                            disabled={a > totalPages}
                            onPress={() => a <= totalPages && setCurrentPage(a)}
                            accessibilityLabel={`Go to page ${a}`}
                            style={[
                              styles.mobilePaginationCell,
                              aIsActive
                                ? styles.mobilePaginationCurrentCell
                                : styles.mobilePaginationNextCell,
                              !aIsActive && isDark && { backgroundColor: colors.background.elevated },
                            ]}
                          >
                            <Text
                              style={
                                aIsActive
                                  ? styles.mobilePaginationCurrentText
                                  : [
                                      styles.mobilePaginationNextText,
                                      isDark && { color: colors.brand.primary },
                                    ]
                              }
                            >
                              {a}
                            </Text>
                          </TouchableOpacity>

                          {/* For single page, show empty cell instead of page 2 */}
                          {isSinglePage ? (
                            <View
                              style={[
                                styles.mobilePaginationCell,
                                styles.mobilePaginationEmptyCell,
                                isDark && { backgroundColor: colors.background.elevated },
                              ]}
                            />
                          ) : (
                            <TouchableOpacity
                              disabled={b > totalPages}
                              onPress={() =>
                                b <= totalPages && setCurrentPage(b)
                              }
                              accessibilityLabel={`Go to page ${b}`}
                              style={[
                                styles.mobilePaginationCell,
                                bIsActive
                                  ? styles.mobilePaginationCurrentCell
                                  : styles.mobilePaginationNextCell,
                                !bIsActive && isDark && { backgroundColor: colors.background.elevated },
                              ]}
                            >
                              <Text
                                style={
                                  bIsActive
                                    ? styles.mobilePaginationCurrentText
                                    : [
                                        styles.mobilePaginationNextText,
                                        isDark && { color: colors.brand.primary },
                                      ]
                                }
                              >
                                {b}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </>
                      );
                    })()}

                    <TouchableOpacity
                      disabled={
                        displayStart + 1 >= totalPages &&
                        currentPage === totalPages
                      }
                      onPress={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        setDisplayStart(newPage);
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart + 1 >= totalPages &&
                          currentPage === totalPages &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-next-page-button"
                      accessibilityLabel="Next page"
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={16}
                        color={
                          displayStart + 1 >= totalPages &&
                          currentPage === totalPages
                            ? "#A0A0A0"
                            : isDark
                            ? colors.brand.primary
                            : "#005CBC"
                        }
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ) : (
              // Table layout for web and iPad
              <LHDataTable
                data={tableData}
                columns={customizedColumns}
                tableHeader=""
                tableDescription={`Showing ${tableData.length} result(s)`}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  metricsWrapper: {
    padding: 0,
    backgroundColor: "#F5F9FF",
    flex: 1,
  },
  headerBox: {
    //alignItems: "flex-start",
    backgroundColor: "transparent",
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  // iOS mobile-only spacing override for header
  headerBoxMobileSpacing: {
    marginBottom: 0,
  },
  // iOS mobile-only layout for filter button on right
  headerBoxIOSMobile: {
    justifyContent: "flex-end",
  },
  headerTitleContainer: {
    flex: 1,
    paddingRight: 8,
  },
  headerTitleMobile: {
    // compact vertical spacing on iOS mobile when filter sits inline
    marginBottom: 10,
  },
  headerTitle: {
    fontFamily: FontFamily.bold,
    fontWeight: "700", // Bold
    fontStyle: "normal",
    fontSize: 24, // Font Size/H5 (approx)
    lineHeight: 28, // Line Height/H5 (approx)
    letterSpacing: 0,
    color: "#000000",
    textAlign: "left",
    marginBottom: 8,
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  filterContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
    fontFamily: FontFamily.regular,
  },
  dataTableContainer: {
    // default (desktop / web / iPad) values — mobile overrides are applied at render time
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: "1%",
    padding: 24,
  },
  dataTableContainerMobile: {
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
  },
  cellText: {
    fontSize: 14,
    color: "#2D2D2D",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    padding: 4,
    fontFamily: FontFamily.regular,
  },
  detailsCell: {
    fontSize: 14,
    color: "#005CBC",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    maxWidth: Platform.OS === "web" ? "100%" : 250,
    fontWeight: 500,
    fontFamily: FontFamily.regular,
  },
  // Card styles for iOS mobile
  cardsContainer: {
    flex: 1,
    backgroundColor: "transparent",
  },
  resultsDescription: {
    fontSize: 14,
    color: "#616161",
    marginBottom: 16,
    textAlign: "left",
    marginHorizontal: 16,
    fontFamily: FontFamily.regular,
  },
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    // marginHorizontal: 16,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  cardSeparator: {
    height: 8,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  cardPrimaryValue: {
    flex: 1,
    marginRight: 12,
  },
  cardPrimaryText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  statusTagText: {
    fontSize: 12,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  statusTagCompleted: {
    backgroundColor: "#28A745",
  },
  statusTagInProgress: {
    backgroundColor: "#E5A408",
  },
  statusTagFailed: {
    backgroundColor: "#C12C01",
  },
  statusTagDefault: {
    backgroundColor: "#6C757D",
  },
  cardContent: {
    marginBottom: 8,
  },
  cardRow: {
    marginBottom: 4,
  },
  cardRowText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  cardRowLabel: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12,
    color: "#616161",
    fontFamily: FontFamily.regular,
  },
  cardRowValue: {
    fontWeight: "400",
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#2D2D2D",
    fontFamily: FontFamily.regular,
  },
  viewDetailsContainer: {
    paddingVertical: 4,
    marginBottom: -6,
    zIndex: 10, // Ensure it's above the tap overlay
  },
  viewDetailsButton: {
    zIndex: 11, // Higher than container
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },

  cardTapOverlay: {
    position: "absolute",
    top: 60, // Start below the header section
    left: 0,
    right: 0,
    bottom: 0, // Will be dynamically adjusted
    backgroundColor: "transparent",
    zIndex: 1, // Higher z-index for reliable touch handling on iOS
  },
  // Mobile pagination styles (copied from LHDataTable)
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationEmptyCell: {
    backgroundColor: "#FBFCFE",
    opacity: 0.3,
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  // Mobile filter button styles
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#034895", // From Figma design
    borderColor: "#034895",
    borderRadius: 24,
    paddingHorizontal: 16,
    height: 32,
    width: 120, // Match Begin button width
    minWidth: 60,
    gap: 4,
    // nudge the button up slightly on iOS mobile; using relative positioning
    position: "relative",
    top: -10,
    zIndex: 2,
    justifyContent: "center",
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
});

const getResponsiveItemsPerRow = (itemsPerRow: number): number => {
  const windowWidth = Dimensions.get("window").width;

  if (Platform.OS === "web") {
    return windowWidth > 1200
      ? Math.min(itemsPerRow, 4)
      : Math.min(itemsPerRow, 3);
  } else if (Platform.OS === "ios" || Platform.OS === "android") {
    if (windowWidth > 768) {
      return Math.min(itemsPerRow, 3);
    }
    return Math.min(itemsPerRow, 2);
  }
  return itemsPerRow;
};

// (iOS mobile detection is computed inside the component as `isMobileIos`)

// Card separator component
const CardSeparator = () => <View style={{ height: 8 }} />;

// Card component for iOS mobile
interface CardItemProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any) => void;
}

const CardItem: React.FC<CardItemProps> = ({ item, columns, onRowClick }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Get the status tag style based on status value. Accepts strings or React elements.
  const getStatusTagStyle = (status: any) => {
    // Extract text from potential React element like <Text>In Progress</Text>
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") {
        raw = children;
      } else if (Array.isArray(children)) {
        raw = children.join("");
      } else {
        raw = String(children);
      }
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "completed") {
      return styles.statusTagCompleted;
    }
    if (normalized.includes("inprogress")) {
      return styles.statusTagInProgress;
    }
    if (normalized === "failed") {
      return styles.statusTagFailed;
    }
    return styles.statusTagDefault;
  };

  // Return explicit background color for a status value to avoid platform style merging issues
  const getStatusTagColor = (status: any) => {
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") raw = children;
      else if (Array.isArray(children)) raw = children.join("");
      else raw = String(children);
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");
    if (normalized === "completed") return "#027C06";
    if (normalized.includes("inprogress")) return "#E5A408";
    if (normalized === "failed") return "#C12C01";
    return "#6C757D";
  };

  // Get the status text color
  const getStatusTextColor = (_status: any) => {
    // all statuses currently use white for text
    return "#FFFFFF";
  };

  // First column (details) - clickable for navigation
  const firstColumn = columns[0];
  const firstValue = firstColumn?.renderCell
    ? firstColumn.renderCell(item)
    : item[firstColumn?.columnKey];

  // Last column (status) - non-clickable
  const lastColumn = columns[columns.length - 1];
  let lastValue: any = item[lastColumn?.columnKey];
  if (lastValue === undefined || lastValue === null) {
    lastValue = "new";
  }

  // All columns for display (skip first and last columns)
  const displayColumns = columns.slice(1, columns.length - 1);

  return (
    <View
      style={[
        styles.cardContainer,
        isDark && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
          borderWidth: 1,
        },
      ]}
    >
      {/* First Row: Primary value (left) + Status tag (right) */}
      <View style={styles.cardHeader}>
        <TouchableOpacity
          onPress={() => onRowClick({ ...item, columnKey: "details" })}
          style={styles.cardPrimaryValue}
        >
          <Text style={[styles.cardPrimaryText, isDark && { color: colors.brand.primary }]} numberOfLines={2}>
            {typeof firstValue === "string"
              ? firstValue
              : item[firstColumn?.columnKey]}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => onRowClick({ ...item, columnKey: "details" })}
          style={[
            styles.statusTag,
            getStatusTagStyle(lastValue),
            { backgroundColor: getStatusTagColor(lastValue) },
          ]}
        >
          <Text
            style={[
              styles.statusTagText,
              { color: getStatusTextColor(lastValue) },
            ]}
          >
            {lastValue}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Content Rows: "Header: Value" format - show all fields */}
      <View style={styles.cardContent}>
        {displayColumns.map((column, index) => {
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          return (
            <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
              <Text style={styles.cardRowText}>
                <Text style={[styles.cardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header.toUpperCase()}:{" "}
                </Text>
                <Text style={[styles.cardRowValue, isDark && { color: colors.text.primary }]}>
                  {typeof value === "string" ? value : value || "-"}
                </Text>
              </Text>
            </View>
          );
        })}
      </View>

      {/* View Details Link - opens overlay when clicked */}
      {displayColumns.length > 0 && (
        <View style={styles.viewDetailsContainer}>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              onRowClick(item);
            }}
            style={styles.viewDetailsButton}
          >
            <Text
              style={[styles.viewDetailsText, isDark && { color: colors.brand.primary }]}
            >
              View Details
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tap anywhere else overlay for modal - positioned to avoid View Details area */}
      <TouchableOpacity
        style={[
          styles.cardTapOverlay,
          {
            bottom: displayColumns.length > 0 ? 40 : 0, // Leave space for View Details
          },
        ]}
        onPress={() => onRowClick(item)}
        activeOpacity={0.1}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      />
    </View>
  );
};
export default SparcWalkMetrics;
